import { observer } from 'mobx-react';
import withStoreProvider from '../../../../util/withStoreProvider';
import {
  useVoucherStore,
  VoucherStore,
  VoucherStoreProvider,
} from './VoucherStore';
import { BaseLayout } from '../../navigation';
import classes from '../../../../sharedStyles/InfoCard.module.css';
import {
  Alert,
  Button,
  Divider,
  FileInput,
  Flex,
  Select,
  Table,
  Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { VoucherType } from '@prisma/client';
import type { BatchOfNewVouchersReqDto } from '@web/common';
import { parseVoucherFile, VoucherFileParseResult } from './parseVoucherFile';

const PREVIEW_LIMIT = 200;
const REJECTED_LIMIT = 10;

type UploadableVoucherType = BatchOfNewVouchersReqDto['type'];

const isUploadableVoucherType = (
  value: string,
): value is UploadableVoucherType =>
  value === VoucherType.TenEuros || value === VoucherType.FiveEuros;

const VoucherContent = observer(() => {
  const store = useVoucherStore();
  const { t } = useTranslation('voucher');
  const [parseResult, setParseResult] = useState<VoucherFileParseResult | null>(
    null,
  );
  const [uploadedCount, setUploadedCount] = useState(0);
  const [selectedType, setSelectedType] = useState<UploadableVoucherType>(
    VoucherType.TenEuros,
  );

  const codes = parseResult?.codes ?? [];

  const typeOptions = [
    { value: VoucherType.TenEuros, label: t('TypeSelect.TenEuros') },
    { value: VoucherType.FiveEuros, label: t('TypeSelect.FiveEuros') },
  ];

  const readFileAndValidate = (file: File | null) => {
    if (!file) {
      return;
    }
    store.clear();
    setParseResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setParseResult(parseVoucherFile(content));
    };
    reader.readAsText(file, 'UTF-8');
  };

  const uploadVouchers = () => {
    if (codes.length === 0) {
      return;
    }
    setUploadedCount(codes.length);
    store.batchInsertVouchers({ type: selectedType, codes });
  };

  useEffect(() => {
    if (store.creationResult) {
      setParseResult(null);
    }
  }, [store.creationResult, store.creationError]);

  useEffect(() => {
    store.getVoucherLevels();
  }, [store]);

  return (
    <BaseLayout>
      <Flex
        direction="column"
        gap={20}
        bg="#fff"
        p={40}
        className={classes.borderRadius}
        style={{ 'max-height': '80vh' }}
      >
        <Text fw="500" size="28px" lh="24px">
          {t('Title')}
        </Text>
        <Divider />
        <Text size="26px">{t(`VoucherLevels.Title`)}</Text>
        <Text>
          {t(`VoucherLevels.Current`, { count: store.voucherLevels?.current })}
        </Text>
        {(['TenEuros', 'FiveEuros'] as const).map((type) => {
          const count = store.voucherLevels?.currentByType[type];
          return (
            <Text key={type} pl={20}>
              {t(`TypeSelect.${type}`)}: {count}
              {count === 0 ? ' ⚠️' : ''}
            </Text>
          );
        })}
        <Text>
          {t(`VoucherLevels.LastWeek`, {
            count: store.voucherLevels?.lastWeek,
          })}
        </Text>
        <Text>
          {t(`VoucherLevels.LastMonth`, {
            count: store.voucherLevels?.lastMonth,
          })}
        </Text>
        <Divider />
        <Select
          label={t('TypeSelect.Label')}
          data={typeOptions}
          value={selectedType}
          onChange={(value) =>
            value && isUploadableVoucherType(value) && setSelectedType(value)
          }
          allowDeselect={false}
          style={{ width: '23em' }}
        />
        <FileInput
          onChange={readFileAndValidate}
          accept=".csv,.txt"
          label={t('FileUploadButtonTitle')}
          description={t('SelectFileDescription')}
          placeholder={t('SelectFilePlaceholder')}
          style={{ width: '23em' }}
        />

        {parseResult !== null && (
          <Alert color={codes.length === 0 ? 'red' : undefined}>
            <Text>
              {codes.length === 0
                ? t('ParseSummary.NothingFound')
                : t('ParseSummary.Detected', { count: codes.length })}
            </Text>
            {parseResult.skippedHeader !== null && (
              <Text size="12px">
                {t('ParseSummary.SkippedHeader', {
                  header: parseResult.skippedHeader,
                })}
              </Text>
            )}
            {parseResult.duplicatesInFile > 0 && (
              <Text size="12px">
                {t('ParseSummary.DuplicatesInFile', {
                  count: parseResult.duplicatesInFile,
                })}
              </Text>
            )}
            {parseResult.rejected.length > 0 && (
              <>
                <Text size="12px" mt={10}>
                  {t('ParseSummary.Rejected', {
                    count: parseResult.rejected.length,
                  })}
                </Text>
                {parseResult.rejected
                  .slice(0, REJECTED_LIMIT)
                  .map((rejected) => (
                    <Text size="12px" key={rejected.line}>
                      {t('ParseSummary.RejectedLine', rejected)}
                    </Text>
                  ))}
                {parseResult.rejected.length > REJECTED_LIMIT && (
                  <Text size="12px">
                    {t('ParseSummary.MoreRejected', {
                      count: parseResult.rejected.length - REJECTED_LIMIT,
                    })}
                  </Text>
                )}
              </>
            )}
          </Alert>
        )}

        <Divider />
        <Text size="16px">{t('VoucherTable.Description')}</Text>
        <div style={{ overflowY: 'auto' }}>
          <Table stickyHeader highlightOnHover highlightOnHoverColor="#f6f5ef">
            <Table.Thead>
              <Table.Tr>
                <Table.Td>{t('VoucherTable.Columns.VoucherCode')}</Table.Td>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {codes.slice(0, PREVIEW_LIMIT).map((code) => (
                <Table.Tr key={code}>
                  <Table.Td>{code}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {codes.length > PREVIEW_LIMIT && (
            <Text size="12px" mt={10}>
              {t('VoucherTable.MoreRows', {
                count: codes.length - PREVIEW_LIMIT,
              })}
            </Text>
          )}
        </div>
        <Divider />
        <Text size="12px">{t('VoucherButtonInfoText')}</Text>
        <Button
          onClick={uploadVouchers}
          disabled={!!store.creationError || codes.length === 0}
          style={{ width: '25em', minHeight: '3em' }}
        >
          {t('UploadButton')}
        </Button>
        {store.creationResult !== null && (
          <Alert>
            {t('SuccessMessage', {
              count: store.creationResult.vouchersCreated,
              total: uploadedCount,
              notAdded: uploadedCount - store.creationResult.vouchersCreated,
            })}
          </Alert>
        )}
        {store.creationError !== null && (
          <Alert color="red" mt={20} mih="5em">
            {t('ErrorMessage')}
          </Alert>
        )}
      </Flex>
    </BaseLayout>
  );
});

export const VoucherView = withStoreProvider(
  new VoucherStore(),
  VoucherStoreProvider,
)(VoucherContent);
